// ═══════════════════════════════════════════════════════════════════════════════
// MyWorkstation.jsx
// Tab 1 of the HomeHub authenticated experience
// The researcher's personal command center
// ═══════════════════════════════════════════════════════════════════════════════
import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api/client';

const C = {
  void:'#020508', deep:'#060b14', surface:'#0a1120',
  b0:'#0f1a2e', b1:'#1e293b', b2:'#334155',
  t1:'#e2e8f0', t2:'#b6c3d8', t3:'#8396b3', t4:'#627795',
  cyan:'#22d3ee', violet:'#a78bfa', green:'#4ade80',
  gold:'#fbbf24', red:'#f87171', orange:'#fb923c', pink:'#f472b6',
  mono:"'JetBrains Mono',monospace",
  syne:"'Syne',sans-serif",
};

const MODULES = [
  { id:'auto_eda',           label:'AUTO EDA',            icon:'📊', color:C.cyan,   cap:10,  accept:'CSV / Excel',     desc:'Cleaning · Distributions · Stats · Report' },
  { id:'ml_experiments',     label:'ML EXPERIMENTS',      icon:'⚡', color:C.green,  cap:10,  accept:'CSV / Excel',     desc:'Train · Compare Models · SHAP · Report' },
  { id:'dl_experiments',     label:'DL EXPERIMENTS',      icon:'🧠', color:C.violet, cap:10,  accept:'CSV / Excel',     desc:'ANN Builder · Init · Train · Test · Metrics' },
  { id:'doc_analysis',       label:'DOC ANALYSIS',        icon:'📄', color:C.violet, cap:10,  accept:'PDF / TXT / DOCX', desc:'NLP · Entity Extraction · Summary · Insights' },
  { id:'image_insights',     label:'IMAGE INSIGHTS',      icon:'🖼️', color:C.pink,   cap:100, accept:'PNG / JPG / WEBP', desc:'Vision · OCR · Classification · Report' },
  { id:'audio_video',        label:'AUDIO/VIDEO STUDIO',  icon:'🎥', color:C.gold,   cap:10,  accept:'MP3 / WAV / MP4', desc:'Timeline · Transcription · Frame Analysis' },
];

const relTime = iso => {
  if (!iso) return '';
  const s = (Date.now() - new Date(iso)) / 1000;
  if (s < 60)     return 'just now';
  if (s < 3600)   return `${Math.floor(s/60)}m ago`;
  if (s < 86400)  return `${Math.floor(s/3600)}h ago`;
  if (s < 604800) return `${Math.floor(s/86400)}d ago`;
  return new Date(iso).toLocaleDateString('en',{month:'short',day:'numeric'});
};

const wsSubline = ws => {
  const s = ws.summary || {};
  return [
    s.filename,
    s.rows    && `${Number(s.rows).toLocaleString()} rows`,
    s.cols    && `${s.cols} cols`,
    s.word_count && `${Number(s.word_count).toLocaleString()} words`,
    s.pages   && `${s.pages}p`,
    s.best_score && `best ${(s.best_score).toFixed(1)}%`,
  ].filter(Boolean).join(' · ');
};

function greet(name) {
  const h = new Date().getHours();
  const p = h < 12 ? 'Morning' : h < 17 ? 'Afternoon' : 'Evening';
  return name ? `${p}, ${name.split(' ')[0]}` : p;
}

// ─── Tiny atoms ───────────────────────────────────────────────────────────────

function Chip({ label, color, tiny }) {
  return (
    <span style={{
      fontFamily: C.mono, fontSize: tiny ? 7 : 8, color,
      background: `${color}14`, border: `1px solid ${color}30`,
      borderRadius: 2, padding: tiny ? '1px 4px' : '2px 7px',
      letterSpacing: '.07em', fontWeight: 700, whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

function SectionHead({ label, sub, action, onAction, color = C.t4 }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 10 }}>
      <div style={{ display:'flex', alignItems:'baseline', gap: 8 }}>
        <span style={{ fontFamily:C.mono, fontSize:8, color, letterSpacing:'.18em', fontWeight:700 }}>{label}</span>
        {sub && <span style={{ fontFamily:C.mono, fontSize:8, color:C.t4 }}>{sub}</span>}
      </div>
      {action && (
        <button onClick={onAction} style={{
          fontFamily:C.mono, fontSize:8, color:C.t4, background:'transparent',
          border:'none', cursor:'pointer', letterSpacing:'.08em',
          transition:'color .12s'
        }}
          onMouseEnter={e => e.currentTarget.style.color = C.cyan}
          onMouseLeave={e => e.currentTarget.style.color = C.t4}
        >{action}</button>
      )}
    </div>
  );
}

// ─── Workspace card ───────────────────────────────────────────────────────────

function WsCard({ ws, onResume, onDelete }) {
  const mod  = MODULES.find(m => m.id === ws.module_id) || { label: ws.module_id, icon:'◈', color:C.t3 };
  const sub  = wsSubline(ws);
  const note = ws.summary?.findings || ws.summary?.note || '';
  const [hov,  setHov]  = useState(false);
  const [conf, setConf] = useState(false);

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => { setHov(false); setConf(false); }}
      onClick={() => !conf && onResume(ws)}
      style={{
        position: 'relative', cursor: 'pointer', borderRadius: 4,
        padding: '14px 14px 40px',
        background: hov ? `${mod.color}09` : C.surface,
        border:     `1px solid ${hov ? mod.color + '55' : C.b1}`,
        borderTop:  `2px solid ${mod.color}`,
        transition: 'all .15s',
      }}
    >
      {/* Header row */}
      <div style={{ display:'flex', alignItems:'center', gap:5, marginBottom:8 }}>
        <span style={{ color:mod.color, fontSize:11, flexShrink:0 }}>{mod.icon}</span>
        <Chip label={mod.label} color={mod.color} tiny />
        <span style={{ marginLeft:'auto', fontFamily:C.mono, fontSize:8, color:C.t4 }}>
          {relTime(ws.updated_at)}
        </span>
      </div>

      {/* Name */}
      <div style={{
        fontFamily: C.syne, fontWeight:700, fontSize:13, color:C.t1,
        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:5,
      }}>{ws.label}</div>

      {/* Data stats */}
      {sub && (
        <div style={{ fontFamily:C.mono, fontSize:9, color:C.t4, marginBottom: note ? 4 : 0 }}>
          {sub}
        </div>
      )}

      {/* Finding snippet */}
      {/* {note && (
        <div style={{
          fontFamily:C.mono, fontSize:9, color:C.t3, lineHeight:1.55,
          display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden',
          marginTop:3,
        }}>{note}</div>
      )} */}

      {/* Active tab pill */}
      {ws.active_tab && (
        <div style={{ position:'absolute', bottom:10, left:14 }}>
          <span style={{
            fontFamily:C.mono, fontSize:7, color:mod.color,
            background:`${mod.color}12`, border:`1px solid ${mod.color}2a`,
            borderRadius:2, padding:'2px 5px', letterSpacing:'.06em',
          }}>{ws.active_tab}</span>
        </div>
      )}

      {/* Hover actions */}
      {hov && (
        <div style={{ position:'absolute', bottom:8, right:8, display:'flex', gap:3 }}>
          {!conf ? (
            <>
              <button onClick={e => { e.stopPropagation(); onResume(ws); }} style={{
                fontFamily:C.mono, fontSize:8, color:mod.color,
                background:`${mod.color}18`, border:`1px solid ${mod.color}44`,
                borderRadius:2, padding:'3px 8px', cursor:'pointer', fontWeight:700,
              }}>RESUME →</button>
              <button onClick={e => { e.stopPropagation(); setConf(true); }} style={{
                fontFamily:C.mono, fontSize:8, color:C.t4, background:'transparent',
                border:`1px solid ${C.b2}`, borderRadius:2, padding:'3px 6px', cursor:'pointer',
              }}>✕</button>
            </>
          ) : (
            <>
              <span style={{ fontFamily:C.mono, fontSize:8, color:C.red, lineHeight:'22px', paddingRight:4 }}>Delete?</span>
              <button onClick={e => { e.stopPropagation(); onDelete(ws.id); }} style={{
                fontFamily:C.mono, fontSize:8, color:C.red, background:'rgba(248,113,113,.1)',
                border:`1px solid ${C.red}44`, borderRadius:2, padding:'3px 8px', cursor:'pointer',
              }}>YES</button>
              <button onClick={e => { e.stopPropagation(); setConf(false); }} style={{
                fontFamily:C.mono, fontSize:8, color:C.t4, background:'transparent',
                border:`1px solid ${C.b2}`, borderRadius:2, padding:'3px 6px', cursor:'pointer',
              }}>NO</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Module launch card ───────────────────────────────────────────────────────

function ModCard({ mod, used, isPremium, onLaunch }) {
  const [hov, setHov] = useState(false);
  const pct     = isPremium ? 0 : Math.min(100, Math.round((used / mod.cap) * 100));
  const atLimit = !isPremium && used >= mod.cap;
  const near    = !isPremium && pct >= 80 && !atLimit;

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={() => !atLimit && onLaunch(mod.id)}
      style={{
        borderRadius: 4, padding: '13px 14px',
        background: hov && !atLimit ? `${mod.color}0d` : C.surface,
        border: `1px solid ${atLimit ? C.red + '33' : hov ? mod.color + '55' : C.b1}`,
        cursor: atLimit ? 'not-allowed' : 'pointer',
        opacity: atLimit ? .55 : 1,
        transition: 'all .15s',
        display: 'flex', flexDirection: 'column', gap: 9,
      }}
    >
      <div style={{ display:'flex', alignItems:'center', gap:9 }}>
        <div style={{
          width:30, height:30, borderRadius:3, flexShrink:0,
          background:`${mod.color}15`, border:`1px solid ${mod.color}30`,
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:13, color:mod.color, fontWeight:800,
        }}>{mod.icon}</div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontFamily:C.mono, fontSize:9, color:mod.color,
            fontWeight:700, letterSpacing:'.07em', marginBottom:1 }}>{mod.label}</div>
          <div style={{ fontFamily:C.mono, fontSize:8, color:C.t4,
            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{mod.accept}</div>
        </div>
        {atLimit  ? <Chip label="FULL"  color={C.red}   tiny /> :
         hov      ? <span style={{ fontFamily:C.mono, fontSize:11, color:mod.color }}>→</span> : null}
      </div>

      <div style={{ fontFamily:C.mono, fontSize:9, color:C.t3, lineHeight:1.6 }}>{mod.desc}</div>

      {/* Free-tier usage bar */}
      {!isPremium && (
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
            <span style={{ fontFamily:C.mono, fontSize:8,
              color: atLimit ? C.red : near ? C.gold : C.t4 }}>
              {used} / {mod.cap} used
            </span>
            {near && !atLimit && (
              <span style={{ fontFamily:C.mono, fontSize:8, color:C.gold }}>↑ almost full</span>
            )}
          </div>
          <div style={{ height:2, background:C.b1, borderRadius:2, overflow:'hidden' }}>
            <div style={{
              height:'100%', width:`${pct}%`, borderRadius:2,
              background: atLimit ? C.red : near ? C.gold : mod.color,
              transition: 'width .7s ease',
            }}/>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color, accent }) {
  return (
    <div style={{
      background: C.surface, border:`1px solid ${C.b1}`,
      borderTop: `2px solid ${color}`, borderRadius:4, padding:'12px 14px',
    }}>
      <div style={{ fontFamily:C.mono, fontSize:8, color:C.t4, letterSpacing:'.12em', marginBottom:5 }}>{label}</div>
      <div style={{ fontFamily:C.syne, fontWeight:800, fontSize:22, color, lineHeight:1 }}>{value}</div>
      {sub && <div style={{ fontFamily:C.mono, fontSize:8, color:C.t4, marginTop:4 }}>{sub}</div>}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function MyWorkstation({ authDisplayName, authUser, isPremium, usageSummary, onNavigate, onUpgrade }) {
  const [workspaces, setWs]     = useState([]);
  const [wsLoading,  setWsLoad] = useState(true);
  const [mounted,    setMount]  = useState(false);

  useEffect(() => { const t = setTimeout(() => setMount(true), 50); return () => clearTimeout(t); }, []);

  const loadWs = useCallback(async () => {
    setWsLoad(true);
    try   { const r = await api.workspace.list(); setWs(r.workspaces || []); }
    catch {}
    finally { setWsLoad(false); }
  }, []);

  useEffect(() => { loadWs(); }, [loadWs]);
  useEffect(() => {
    const h = () => loadWs();
    window.addEventListener('dd-workspace-saved', h);
    return () => window.removeEventListener('dd-workspace-saved', h);
  }, [loadWs]);

  const handleResume = useCallback(ws => {
    window.dispatchEvent(new CustomEvent('dd-workspace-restore', { detail: ws }));
  }, []);

  const handleDelete = useCallback(async id => {
    try { await api.workspace.remove(id); setWs(p => p.filter(w => w.id !== id)); } catch {}
  }, []);

  const mods = usageSummary?.modules || {};
  const tier = authUser?.tier || 'free';
  const totalRuns  = Object.values(mods).reduce((s, m) => s + (m.done || 0), 0);
  const nextReset  = usageSummary?.next_reset_at;

  const fd = (d = 0) => ({
    opacity:   mounted ? 1 : 0,
    transform: mounted ? 'none' : 'translateY(7px)',
    transition:`opacity .4s ease ${d}s, transform .4s ease ${d}s`,
  });

  return (
    <div style={{ padding:'0 0 60px', maxWidth:1040, margin:'0 auto' }}>
      <style>{`
        .ws-ws-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(220px,1fr)); gap:10px; }
        .ws-mod-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; }
        .ws-stat-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:8px; }
        @media(max-width:860px){
          .ws-mod-grid { grid-template-columns:repeat(2,1fr); }
          .ws-stat-grid { grid-template-columns:repeat(2,1fr); }
          .ws-body { grid-template-columns:1fr; }
        }
        @media(max-width:500px){
          .ws-mod-grid { grid-template-columns:1fr; }
          .ws-ws-grid  { grid-template-columns:1fr; }
        }
      `}</style>

      {/* ══ HERO HEADER ══════════════════════════════════════════════════════ */}
      <div style={{
        ...fd(0),
        padding: '24px 24px 20px',
        borderBottom: `1px solid ${C.b1}`,
        marginBottom: 24,
        background: `linear-gradient(180deg, ${C.deep} 0%, transparent 100%)`,
      }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:12 }}>
          <div>
           
            <div style={{ fontFamily:C.syne, fontWeight:800, fontSize:'clamp(20px,3vw,30px)', color:C.t1, lineHeight:1.05, marginBottom:5 }}>
              {greet(authDisplayName)}
            </div>
            <div style={{ fontFamily:C.mono, fontSize:9, color:C.t4 }}>
              {wsLoading ? '...' :
                workspaces.length > 0
                  ? `${workspaces.length} active experiment${workspaces.length !== 1 ? 's' : ''} · ${totalRuns} analyses this month`
                  : 'no experiments yet — start your first analysis below'
              }
            </div>
          </div>
          <div style={{ display:'flex', gap:6, alignItems:'center' }}>
            {!isPremium && (
              <button onClick={onUpgrade} style={{
                fontFamily:C.mono, fontSize:8, fontWeight:700, color:C.gold,
                background:'rgba(251,191,36,.09)', border:`1px solid ${C.gold}35`,
                borderRadius:3, padding:'6px 12px', cursor:'pointer',
                letterSpacing:'.08em', transition:'background .15s',
              }}
                onMouseEnter={e => e.currentTarget.style.background='rgba(251,191,36,.18)'}
                onMouseLeave={e => e.currentTarget.style.background='rgba(251,191,36,.09)'}
              >⚡ UPGRADE</button>
            )}
            <div style={{
              fontFamily:C.mono, fontSize:8, fontWeight:700, letterSpacing:'.1em',
              color: isPremium ? C.gold : C.t4,
              background: isPremium ? 'rgba(251,191,36,.09)' : C.b0,
              border: `1px solid ${isPremium ? C.gold+'30' : C.b1}`,
              borderRadius:3, padding:'6px 12px',
            }}>
              {isPremium ? `⚡ ${tier.toUpperCase()}` : 'FREE TIER'}
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding:'0 24px' }}>

        {/* ══ STATS ROW ════════════════════════════════════════════════════════ */}
        <div style={{ ...fd(0.05), marginBottom:24 }}>
          <div className="ws-stat-grid">
            <StatCard label="EXPERIMENTS"   value={wsLoading ? '…' : workspaces.length} sub={isPremium ? 'of 50' : 'of 3 free'}             color={C.cyan}   />
            <StatCard label="ANALYSES"     value={totalRuns}                            sub="this month"                                     color={C.green}  />
            <StatCard label="PRICING"  value={isPremium ? tier.toUpperCase() : 'FREE TIER'} sub={isPremium ? 'unlimited analyses' : 'Limits Apply'} color={isPremium ? C.gold : C.t3} />
            <StatCard label="RANK"  value= "N/A" sub="Not Ranked Yet" color={C.violet} />
          </div>
        </div>

        <div className="ws-body">
          {/* ── LEFT COLUMN ──────────────────────────────────────────────── */}
          <div>

            {/* ══ SAVED EXPERIMENTS ═════════════════════════════════════════ */}
            <div style={{ ...fd(0.08), marginBottom:28 }}>
              <SectionHead
                label={workspaces.length > 0 ? 'CONTINUE RESEARCH' : 'NO ACTIVE EXPERIMENTS'}
                sub={!wsLoading && workspaces.length > 0 ? `${workspaces.length} saved` : undefined}
                action={workspaces.length > 0 ? 'hover to manage' : undefined}
                color={workspaces.length > 0 ? C.cyan : C.t4}
              />

              {wsLoading ? (
                <div className="ws-ws-grid">
                  {[0,1,2].map(i => (
                    <div key={i} style={{
                      height:110, borderRadius:4,
                      background:C.surface, border:`1px solid ${C.b1}`,
                      animation:'wsPulse 1.3s ease-in-out infinite',
                      animationDelay:`${i * .15}s`,
                    }}/>
                  ))}
                </div>
              ) : workspaces.length === 0 ? (
                <div style={{
                  background:C.surface, border:`1px solid ${C.b1}`,
                  borderLeft:`3px solid ${C.cyan}44`,
                  borderRadius:4, padding:'18px 20px',
                  display:'flex', alignItems:'flex-start', gap:14,
                }}>
                  <div style={{ fontSize:26, opacity:.3, flexShrink:0, lineHeight:1 }}>⬡</div>
                  <div>
                    <div style={{ fontFamily:C.syne, fontWeight:700, fontSize:13, color:C.t2, marginBottom:5 }}>
                      No Experiments saved yet
                    </div>
                    <div style={{ fontFamily:C.mono, fontSize:9, color:C.t4, lineHeight:1.7 }}>
                      Run an analysis on any module below → click{' '}
                      <span style={{ color:C.cyan, fontWeight:700 }}>Save Experiment</span>{' '}
                      to pin your research context here.{' '}
                      <span style={{ color:C.t3 }}>Your data files stay on your machine.</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="ws-ws-grid">
                  {workspaces.map((ws, i) => (
                    <div key={ws.id} style={fd(0.06 + i * .04)}>
                      <WsCard ws={ws} onResume={handleResume} onDelete={handleDelete} />
                    </div>
                  ))}
                  {/* Empty slot indicator */}
                  {workspaces.length < (isPremium ? 50 : 3) && (
                    <div style={{
                      background:'transparent',
                      border:`1px dashed ${C.b2}`,
                      borderRadius:4, minHeight:100,
                      display:'flex', flexDirection:'column',
                      alignItems:'center', justifyContent:'center',
                      gap:5, opacity:.4,
                    }}>
                      <span style={{ fontSize:20, color:C.t4 }}>＋</span>
                      <span style={{ fontFamily:C.mono, fontSize:8, color:C.t4, textAlign:'center' }}>
                        {isPremium ? `${50 - workspaces.length} slots left` : `${3 - workspaces.length} of 3 free`}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ══ MODULE LAUNCHER ══════════════════════════════════════════ */}
            <div style={fd(0.12)}>
              <SectionHead
                label="ANALYSIS MODULES"
                sub="local compute · data stays on your machine"
                color={C.t4}
              />
              <div className="ws-mod-grid">
                {MODULES.map((mod, i) => (
                  <div key={mod.id} style={fd(0.12 + i * .03)}>
                    <ModCard
                      mod={mod}
                      used={mods[mod.id]?.done || 0}
                      isPremium={isPremium}
                      onLaunch={onNavigate}
                    />
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* ── RIGHT SIDEBAR ─────────────────────────────────────────── */}
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>

                <br></br>
            {/* Usage breakdown */}
            {!isPremium && (
              <div style={{ ...fd(0.13), background:C.surface, border:`1px solid ${C.b1}`, borderRadius:4, padding:'14px' }}>
                <SectionHead
                  label="USAGE THIS MONTH"
                  sub={nextReset ? `resets ${new Date(nextReset).toLocaleDateString('en',{month:'short',day:'numeric'})}` : undefined}
                />
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {MODULES.map(mod => {
                    const used = mods[mod.id]?.done || 0;
                    const pct  = Math.min(100, Math.round((used / mod.cap) * 100));
                    return (
                      <div key={mod.id}>
                        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
                          <span style={{ fontFamily:C.mono, fontSize:8, color:mod.color, letterSpacing:'.06em' }}>
                            {mod.icon} {mod.label.split(' ')[0]}
                          </span>
                          <span style={{ fontFamily:C.mono, fontSize:8, color: pct >= 100 ? C.red : pct >= 80 ? C.gold : C.t4 }}>
                            {used}/{mod.cap}
                          </span>
                        </div>
                        <div style={{ height:2, background:C.b1, borderRadius:2, overflow:'hidden' }}>
                          <div style={{
                            height:'100%', width:`${pct}%`,
                            background: pct >= 100 ? C.red : pct >= 80 ? C.gold : mod.color,
                            borderRadius:2, transition:'width .7s ease',
                          }}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button onClick={onUpgrade} style={{
                  marginTop:12, width:'100%',
                  fontFamily:C.mono, fontSize:9, fontWeight:800,
                  color:C.void, background:C.gold, border:'none',
                  borderRadius:3, padding:'8px', cursor:'pointer',
                  letterSpacing:'.08em', transition:'opacity .15s',
                }}
                  onMouseEnter={e => e.currentTarget.style.opacity='.85'}
                  onMouseLeave={e => e.currentTarget.style.opacity='1'}
                >
                  REMOVE LIMITS → UPGRADE 
                </button>
              </div>
            )}

            

          </div>
        </div>
      </div>

      <style>{`
        @keyframes wsPulse { 0%,100%{opacity:.4;} 50%{opacity:.7;} }
      `}</style>
    </div>
  );
}
